import type { ActionTree } from 'vuex'

import type { MessageListState } from './types'
import { getFilteredRecipientListCount, getMergedFilters } from './utils'
import {
  capitalizeFirstLetter,
  filterObjectToQueryString,
} from '@/utils/helpers'
import type { RootState } from '@/store/modules/types'
import type { MessageList } from '@/api/message-lists/types'
import { getContext } from '@/utils/general'
import { requestApi } from '@/utils/requestApi'

import { clone } from '@/utils/helpers/'

export const messageListActions: ActionTree<MessageListState, RootState> = {
  async FETCH_MORE_MESSAGE_LISTS(
    { state, rootState, commit },
    {
      top = 12,
      skip = false,
      orderBy = { key: 'oid', order: 'desc' },
      type = 'all',
      filterString = '',
      channels = [],
      oids = [],
      select = null,
      reload = false,
      userDefined = true,
      fetchCount = false,
    },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account

    if (state.isFetchingMessageLists) {
      return
    }

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_MESSAGE_LISTS', true)
      const uri = `/promoter/${promoterOid}/message-list`

      const filterObject: any = {
        expressions: [],
        logicalOperators: [],
      }
      if (
        channels.length === 0
        && (userDefined === false || userDefined === true)
      ) {
        filterObject.expressions.push({
          key: 'userDefined',
          operator: userDefined ? '=' : '!=',
          value: true,
        })
        filterObject.logicalOperators.push('AND')
      }
      if (filterString) {
        filterObject.expressions.push({
          key: 'name',
          operator: 'ILIKE',
          value: `%${filterString}%`,
        })
        filterObject.logicalOperators.push('AND')
      }
      if (type === 'auto' || type === 'manual') {
        filterObject.expressions.push({
          key: 'filterGroupOid',
          operator: type === 'auto' ? 'IS NOT' : 'IS',
          value: 'NULL',
        })
        filterObject.logicalOperators.push('AND')
      }
      if (userDefined === true || userDefined === false) {
        filterObject.expressions.push({
          key: 'userDefined',
          operator: '=',
          value: userDefined,
        })
        filterObject.logicalOperators.push('AND')
      }

      if (Array.isArray(oids) && oids.length > 0) {
        oids.forEach((oid) => {
          filterObject.expressions.push({
            key: 'oid',
            operator: '=',
            value: oid,
          })
          filterObject.logicalOperators.push('OR')
        })
      }

      if (channels.length > 0) {
        const subFilterObject: any = {
          expressions: [],
          logicalOperators: [],
        }
        channels.forEach((channel: any) => {
          subFilterObject.expressions.push({
            key: channel,
            operator: '=',
            value: true,
          })
          subFilterObject.logicalOperators.push('OR')
        })
        subFilterObject.logicalOperators.splice(-1, 1)

        filterObject.expressions.push(subFilterObject)
        filterObject.logicalOperators.push('AND')
      }

      filterObject.logicalOperators.splice(-1, 1)

      const params = {
        $top: top,
        $skip: skip ?? (reload ? 0 : state.messageLists.length),
        $orderby: `${orderBy.key} ${orderBy.order}`,
        $select: select ? select : null,
        $filter: filterObjectToQueryString(filterObject),
      }

      if (fetchCount) {
        // @ts-ignore
        params.$count = true
      }

      const { data } = await $axios.get(uri, {
        params: params,
      })
      let rows = []

      if (fetchCount) {
        rows = data.rows
        commit('SET_TOTAL_MESSAGE_LIST_COUNT', data.count)
      }
      else {
        rows = data
      }

      if (reload) {
        commit('SET_MESSAGE_LISTS', rows)
      }
      else {
        commit('CONCAT_MESSAGE_LISTS', rows)
      }

      if (rows.length < top) {
        commit('SET_HAS_NO_MORE_MESSAGE_LISTS', true)
      }
      return rows
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch lists' })
      commit('SET_HAS_FETCH_MESSAGE_LISTS_FAILED', true)
      return false
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGE_LISTS', false)
    }
  },
  async FETCH_CURRENT_MESSAGE_LIST(
    { rootState, commit },
    { oid, allowDeleted = false },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account

    const { $axios, $arNotification } = getContext()

    if (isNaN(oid)) {
      $arNotification.push({
        type: 'warning',
        message: `Cannot fetch message list with ID ${oid}`,
      })
      return null
    }

    try {
      commit('SET_IS_FETCHING_MESSAGE_LIST', true)
      const { data } = await $axios.get(
        `/promoter/${promoterOid}/message-list/${oid}`,
        {
          params: {
            $filter: allowDeleted
              ? `oid=${oid} AND (sysActivep=false OR sysActivep)`
              : null,
          },
        },
      )

      commit('SET_CURRENT_MESSAGE_LIST', data)

      if (data.filterGroupOid && data.filterGroup && !data.filterGroup.name) {
        $arNotification.push({
          type: 'warning',
          message:
            'The segment associated with this list is no longer available',
        })
      }

      return true
    }
    catch (error) {
      commit('RESET_CURRENT_MESSAGE_LIST')
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch list',
      })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGE_LIST', false)
    }
  },
  async CREATE_MESSAGE_LIST(
    { commit, rootState },
    {
      name,
      sms,
      email,
      facebookMessenger,
      meta,
      filterGroupOid,
      skipNavigation = false,
    },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid, oid } = rootState.authModule.account

    const defaultData = {
      promoterOid,
      promoterAccountOid: oid,
      userDefined: true,
      sysActivep: true,
    }

    const messageListData = {
      name,
      sms,
      email,
      facebookMessenger,
      meta,
      filterGroupOid: filterGroupOid || null,
      ...defaultData,
    }

    const { $axios, $arNotification, $router } = getContext()

    try {
      commit('SET_IS_CREATING_MESSAGE_LIST', true)
      const { data } = await $axios.post(
        `/promoter/${promoterOid}/message-list`,
        messageListData,
      )

      $arNotification.push({
        type: 'success',
        message: 'Successfully created list',
      })
      // if (email) {
      //   $arNotification.push({
      //     type: 'success',
      //     message: `We may send you a verification email at ${meta.email.emailAddress} if you haven't verified that address before. Please check your inbox.`,
      //     timeout: 15000,
      //   });
      // }

      // Only navigate if skipNavigation is not set
      if (!skipNavigation) {
        $router.push({
          path: `/message-center/lists/${data.oid}/contacts`,
        })
      }

      // Return the created list data
      return data
    }
    catch (error: any) {
      console.error(error)
      if (error.response) {
        if (error.response.status === 409) {
          $arNotification.push({
            type: 'error',
            message:
              'Could not create new list. Please ensure that you have chosen a unique name and filter',
          })
        }
        else {
          $arNotification.push({
            type: 'error',
            message: 'Failed to save list',
          })
        }
      }
      return false
    }
    finally {
      commit('SET_IS_CREATING_MESSAGE_LIST', false)
    }
  },
  async UPDATE_MESSAGE_LIST({ rootState, commit, state }, { oid, changes }) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_UPDATING_MESSAGE_LIST', true)
      await $axios.patch(
        `/promoter/${promoterOid}/message-list/${oid}`,
        changes,
      )

      commit('PATCH_IN_MESSAGE_LISTS', { oid, changes })
      if (state.currentMessageList && state.currentMessageList.oid === oid) {
        commit('PATCH_CURRENT_MESSAGE_LIST', changes)
      }

      $arNotification.push({
        type: 'success',
        message: 'Successfully updated list',
      })
      return true
    }
    catch (error: any) {
      console.error(error)
      if (error.response && error.response.status === 409) {
        $arNotification.push({
          type: 'error',
          message: 'The name you have entered is already used in another list',
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to update list',
        })
      }
      return false
    }
    finally {
      commit('SET_IS_UPDATING_MESSAGE_LIST', false)
    }
  },
  async DELETE_MESSAGE_LIST({ rootState, commit, state }, messagingListOid) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_DELETING_MESSAGE_LIST', true)
      await $axios.delete(
        `/promoter/${promoterOid}/message-list/${messagingListOid}`,
      )

      commit('REMOVE_FROM_MESSAGE_LISTS', messagingListOid)
      if (
        state.currentMessageList
        && state.currentMessageList.oid === messagingListOid
      ) {
        commit('RESET_CURRENT_MESSAGE_LIST')
      }

      $arNotification.push({
        type: 'success',
        message: 'List has been deleted',
      })
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to delete list' })
      return false
    }
    finally {
      commit('SET_IS_DELETING_MESSAGE_LIST', false)
    }
  },
  async VALIDATE_NEW_MESSAGE_LIST(
    { rootState, commit },
    { name, filterGroupOid },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account

    const filterObject: any = {
      expressions: [],
      logicalOperators: [],
    }
    filterObject.expressions.push({
      key: 'name',
      operator: '=',
      value: name,
    })
    if (filterGroupOid) {
      filterObject.expressions.push({
        key: 'filterGroupOid',
        value: filterGroupOid,
        operator: '=',
      })
      filterObject.logicalOperators.push('OR')
    }

    const { $axios } = getContext()

    try {
      commit('SET_IS_VALIDATING_NEW_MESSAGE_LIST', true)
      const { data } = await $axios.get(
        `/promoter/${promoterOid}/message-list`,
        {
          params: {
            $top: 2,
            $skip: 0,
            $count: true,
            $filter: filterObjectToQueryString(filterObject),
          },
        },
      )
      const messageLists: MessageList[] = data.rows

      const duplicatedName = name
        ? !!messageLists.filter((item) => {
            return item.name === name
          })[0]
        : false
      const duplicatedFilterGroupOid = filterGroupOid
        ? !!messageLists.filter((item) => {
            return item.filterGroupOid === filterGroupOid
          })[0]
        : false

      commit('SET_NEW_MESSAGE_LIST_VALIDATE_RESULT', {
        duplicatedName,
        duplicatedFilterGroupOid,
      })
      return true
    }
    catch (error) {
      console.error(error)
      return false
    }
    finally {
      commit('SET_IS_VALIDATING_NEW_MESSAGE_LIST', false)
    }
  },

  // -------------------------------
  // Operations within message lists

  // Remove a fan from a message list
  async DELETE_FAN_MESSAGE_LIST_SUBSCRIPTION(
    { rootState, commit },
    { fanListOid = null },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    if (!fanListOid) return false

    const uri = `promoter/${promoterOid}/fan-message-list-subscription/${fanListOid}`

    const { $axios } = getContext()

    try {
      return await $axios.delete(uri)
    }
    catch (error) {
      console.error(error)
      throw error
    }
  },

  // Records fan subscription unsubscribe history
  async DELETE_FAN_MESSAGE_LIST_SUBSCRIPTION_HISTORY(
    { rootState, commit },
    { messageListOid, fanOid },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    if (!fanOid || !messageListOid) return false

    const uri = `promoter/${promoterOid}/message-list/${messageListOid}/fan/${fanOid}/fan-message-list-subscription-history`
    const jsonBody: { [k: string]: any } = {
      email: false,
      sms: false,
    }
    const { $axios } = getContext()
    try {
      return await $axios.post(uri, jsonBody)
    }
    catch (error) {
      console.error(error)
      throw error
    }
  },

  async UPDATE_FAN_MESSAGE_LIST_SUBSCRIPTIONS(
    { rootState, commit },
    { fanListOid = null, add = [], remove = [] },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    if (!fanListOid) return false
    if (add.length === 0 && remove.length === 0) return false

    const jsonBody: { [k: string]: any } = {}
    add.forEach((item: string) => {
      jsonBody[item] = true
    })
    remove.forEach((item: string) => {
      jsonBody[item] = false
    })

    const uri = `promoter/${promoterOid}/fan-message-list-subscription/${fanListOid}`

    const { $axios } = getContext()

    try {
      return await $axios.patch(uri, jsonBody)
    }
    catch (error) {
      console.error(error)
      throw error
    }
  },

  // Updates message list subscription history of a fan
  async UPDATE_FAN_MESSAGE_LIST_SUBSCRIPTION_HISTORY(
    { rootState, commit },
    { messageListOid, fanOid, add = [], remove = [] },
  ) {
    if (!rootState.authModule.account) {
      return null
    }
    const { promoterOid } = rootState.authModule.account
    if (!messageListOid || !fanOid) return false
    if (add.length === 0 && remove.length === 0) return false

    const jsonBody: { [k: string]: any } = {}
    add.forEach((item: string) => {
      jsonBody[item] = true
    })
    remove.forEach((item: string) => {
      jsonBody[item] = false
    })

    const uri = `promoter/${promoterOid}/message-list/${messageListOid}/fan/${fanOid}/fan-message-list-subscription-history`

    const { $axios } = getContext()

    try {
      return await $axios.post(uri, jsonBody)
    }
    catch (error) {
      console.error(error)
      throw error
    }
  },

  async SET_GLOBAL_OPT_IN({ rootState, commit }, { fanOid, channels = {} }) {
    if (!rootState.auth.account) {
      return null
    }
    const { promoterOid } = rootState.auth.account
    if (!fanOid) return false

    const uri = `promoter/${promoterOid}/fan/${fanOid}/global-opt-in`
    try {
      return await this.$axios.post(uri, { channels: channels })
    }
    catch (error) {
      this.$arNotification.push({
        type: 'error',
        message: 'Failed to set opt in status',
      })
    }
  },

  // Fetches the current message list's subscriber count with advanced targeting applied to it
  // If the total number if incalculable for any reason, set to NULL.
  // Apologies for all of the ts-ignores - if there's a better solution please let me know...
  async FETCH_FILTERED_RECIPIENT_LIST_COUNT(
    { rootState, rootGetters, getters, state, dispatch, commit },
    {
      channel = null,
      campaignOid = null,
      facebookPageId = null,
      skipStatsSnapshot = false,
    },
  ) {
    if (!rootState.authModule.account) return null
    if (!state.currentMessageList || !state.currentMessageList.oid) return null

    const { promoterOid } = rootState.authModule.account
    const { type, condition, values } = state.advancedMessageListTargeting
    const messageListOid = state.currentMessageList.oid

    const prunedScratchSegment = clone(
      rootGetters['segmentModule/prunedScratchSegment'],
    )
    const currentFilterExpr = clone(getters.getCurrentFilterExpression)

    /* A.D. 20-MAY-2020
     * ------
     * Have to merge the filters coming from the sidebar (via prunedScratchSegment) and the quick filters
     * (coming from getCurrentFilterExpression()) into one filter.
     */
    const targetingFilter = getMergedFilters(
      prunedScratchSegment,
      currentFilterExpr,
    )

    // Helper function to check if targetingFilter is effectively empty
    // When filter was cleared, the conditions and logic arrays are empty array
    const isEmptyTargetingFilter
      = targetingFilter
        && Array.isArray(targetingFilter.conditions)
        && targetingFilter.conditions.length === 0
        && Array.isArray(targetingFilter.logic)
        && targetingFilter.logic.length === 0

    // If targeting is set to 'All' or if it is incomplete, then set the recipient count to the value within
    // current message list. Also set accuracy to 'accurate', as this will be an exact count.
    if (
      !skipStatsSnapshot
      && (type === 'all'
        || !type
        || !condition
        || !values
        || values.length === 0)
      && (!targetingFilter || isEmptyTargetingFilter)
    ) {
      commit('SET_FILTERED_RECIPIENT_LIST_COUNT_ACCURACY', 'accurate')

      if (
        !channel
        || !state.currentMessageList.statsSnapshot
        || !state.currentMessageList.statsSnapshot.total
      ) {
        commit('SET_FILTERED_RECIPIENT_LIST_COUNT', null)
        console.error('Unknown recipient count - this is probably a bug')
        return // Unknown number - we cant accurately predict total recipients without a channel or without stats
      }

      const deDupedName = `valid${capitalizeFirstLetter(channel)}`

      const totalNumber = getFilteredRecipientListCount(
        deDupedName,
        channel,
        campaignOid,
        facebookPageId,
        state.currentMessageList,
      )
      commit('SET_FILTERED_RECIPIENT_LIST_COUNT', totalNumber)

      return
    }

    // EC-3485: Use the filter-fan endpoint for manual email lists (any size).
    // The listSubscriptions condition correctly scopes the count to list members.
    // Filter-group (dynamic) lists must still use the subscribers endpoint because
    // their membership is defined by the filter-group, not fan_message_list_subscription.
    if (channel === 'email' && !state.currentMessageList.filterGroupOid) {
      dispatch('SET_EMAIL_RECIPIENT_COUNT_ESTIMATE', {
        promoterOid,
        filter: targetingFilter,
      })
    }
    else {
      commit('SET_FILTERED_RECIPIENT_LIST_COUNT_ACCURACY', 'accurate')

      // If channel is SMS, then we need to fetch the count from the message-preview endpoint
      // Otherwise, we need to use the subscribers endpoint
      if (channel === 'sms') {
        dispatch('SET_SMS_RECIPIENT_COUNT_ACCURATE', {
          promoterOid,
          filter: targetingFilter,
        })
      }
      else {
        const { $api, $axios, $arNotification } = getContext()

        try {
          commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', true)
          const count = await $api.messageLists.fetchMessageListRecipientCount(
            promoterOid,
            messageListOid,
            targetingFilter,
            channel,
          )
          commit('SET_FILTERED_RECIPIENT_LIST_COUNT', count)
          commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', false)
        }
        catch (error) {
          if ($axios.isCancel(error)) {
            console.error(error)
          }
          else {
            console.error(error)
            $arNotification.push({
              type: 'error',
              message: 'Failed to fetch audience count',
            })
            commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', false)
          }
        }
      }
    }
  },

  async SET_EMAIL_RECIPIENT_COUNT_ESTIMATE(
    { state, commit },
    { promoterOid, filter },
  ) {
    commit('SET_FILTERED_RECIPIENT_LIST_COUNT_ACCURACY', 'estimate')
    commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', true)

    // Modify the targetingFilter to also filter on subscription to the current message list and opt in to email
    if (filter.conditions.length) {
      filter.logic.push('and')
    }

    filter.logic.push('(', 'and', ')')
    filter.conditions.push(
      {
        data: {
          condition: 'true_to_any',
          values: [state.currentMessageList?.oid],
        },
        name: 'listSubscriptions',
        type: 'condition-search-picker',
      },
      {
        data: 'email',
        name: 'subscribedTo',
        type: 'select',
      },
    )

    const { $api, $axios, $arNotification } = getContext()

    try {
      const response = (await requestApi('fetchAudienceCount', {
        params: {
          promoterOid,
          $top: 0,
          $skip: 0,
          $audienceFilter: JSON.stringify(filter),
          $select: 'oid',
          $count: true,
        },
      })) as { count?: number } | undefined
      commit('SET_FILTERED_RECIPIENT_LIST_COUNT', response?.count || 0)
      commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', false)
    }
    catch (error) {
      if ($axios.isCancel(error)) {
        console.error(error)
      }
      else {
        console.error(error)
        $arNotification.push({
          type: 'error',
          message: 'Failed to fetch audience count',
        })
        commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', false)
      }
    }
  },

  async SET_SMS_RECIPIENT_COUNT_ACCURATE(
    { commit, rootState },
    { promoterOid, filter },
  ) {
    // @ts-ignore
    const messageListOid
      = rootState.messageModule.simpleMessageForm?.meta?.messageListOid
        || rootState.messageModule?.currentSelectedMessage?.simpleMessageForm
          ?.meta?.messageListOid
    // @ts-ignore
    const messageBody
      = rootState.messageModule.simpleMessageForm?.meta?.messageBody
        || rootState.messageModule?.currentSelectedMessage?.meta?.messageBody
        || ''

    const { $api, $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', true)
      commit('messageModule/SET_IS_FETCHING_SMS_COST', true, { root: true })
      commit('messageModule/SET_SMS_COST_FAILED_TO_FETCH', false, {
        root: true,
      })

      // @ts-ignore
      const data = await $api.messageLists.fetchSmsMessagePreview(
        promoterOid,
        messageListOid,
        messageBody,
        filter,
      )

      commit('SET_FILTERED_RECIPIENT_LIST_COUNT', data.cost.recipients)
      commit('messageModule/PUT_SMS_MESSAGE_PREVIEW', data, { root: true }) // Lets update the SMS Preview since we have it already...
      commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', false)
      commit('messageModule/SET_IS_FETCHING_SMS_COST', false, { root: true })
    }
    catch (error: any) {
      if ($axios.isCancel(error)) {
        console.error(error)
      }
      else {
        console.error(error)
        $arNotification.push({
          type: 'error',
          message: 'Failed to fetch audience count',
        })
        commit('messageModule/SET_IS_FETCHING_SMS_COST', false, { root: true })
        commit('messageModule/SET_SMS_COST_FAILED_TO_FETCH', true, {
          root: true,
        })
        commit('SET_IS_FETCHING_FILTERED_RECIPIENT_LIST_COUNT', false)
      }
    }
  },

  async FETCH_SUBSCRIPTION_PREFERENCE({ commit }, promoterOid) {
    const { $axios } = getContext()

    try {
      const { data } = await $axios.get(`/promoter/${promoterOid}/property`, {
        params: {
          $filter: 'type=promoter-subscription-preferences',
        },
      })

      if (data.length) {
        commit('SET_PROMOTER_PROPERTY', data[0])
      }

      return true
    }
    catch (error) {
      console.error(error)

      return true
    }
  },

  async UPDATE_SUBSCRIPTION_PREFERENCE(
    { commit, state },
    { promoterOid, enabled },
  ) {
    const formatBool = (val: string) => val.toString().toUpperCase()
    const isSubscriptionPreferenceResource = !!state.promoterProperty?.oid

    const { $axios } = getContext()

    try {
      if (isSubscriptionPreferenceResource) {
        const subscriptionPreferenceOid = state.promoterProperty.oid
        const url = `/promoter/${promoterOid}/property/${subscriptionPreferenceOid}`

        // Patch resource
        const { data } = await $axios.patch(url, {
          property: formatBool(enabled),
        })

        commit('SET_PROMOTER_PROPERTY', {
          oid: subscriptionPreferenceOid,
          property: formatBool(enabled),
        })
      }
      else {
        const url = `/promoter/${promoterOid}/property`

        // Create new resource
        const { data } = await $axios.post(url, {
          property: formatBool(enabled),
          type: 'promoter-subscription-preferences',
          allowMany: false,
          promoterOid,
        })

        commit('SET_PROMOTER_PROPERTY', data)
      }
      return true
    }
    catch (error) {
      console.error(error)

      return true
    }
  },

  async FETCH_MESSAGE_LISTS({ commit, rootState }) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    let messageLists
    try {
      messageLists = await $api.messageLists.getMessageLists(promoterOid)
      commit('SET_MESSAGE_LISTS', messageLists)
    }
    catch (error) {
      console.error(error)
    }
  },
  async FETCH_MESSAGE_LISTS_AND_ATTENDEES(
    { commit, rootState }: any,
    { eventId, campaignId },
  ) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    const finalMessageLists: any = []

    const { $api } = getContext()

    try {
      if (eventId) {
        const eventLists = await $api.messageLists.fetchEventMessageLists(
          promoterOid,
          eventId,
        )
        eventLists.forEach((item) => {
          finalMessageLists.push(item)
        })
      }
      if (campaignId) {
        const campaignLists = await $api.messageLists.fetchCampaignMessageLists(
          promoterOid,
          campaignId,
        )
        campaignLists.forEach((item) => {
          finalMessageLists.push(item)
        })
      }
      const messageLists: []
        = await $api.messageLists.getMessageLists(promoterOid)
      messageLists.forEach((item) => {
        finalMessageLists.push(item)
      })
      commit('SET_MESSAGE_LISTS', finalMessageLists)
    }
    catch (error) {
      console.error(error)
    }
  },

  async FETCH_MESSAGE_LIST_PROGRESS({ commit, rootState }) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    const { $axios, $arNotification } = getContext()
    try {
      const { data, status } = await $axios.get(
        `/promoter/${promoterOid}/task`,
        {
          params: {
            $top: 10,
            $skip: 0,
            $filter: `promoterOid=${promoterOid} AND (status="pending" OR status="in-progress") AND (name ILIKE "%mass-edit%" OR name ILIKE "%background-fan-import-csv%") AND sysActivep=true and messageListOid is not null`,
            $orderby: `started DESC, oid DESC`,
          },
        },
      )

      return data
    }
    catch (error) {
      console.error(error)
    }
  },

  async FETCH_MESSAGE_LIST_PROGRESS_BY_OID(
    { commit, rootState },
    { messageListOid },
  ) {
    const promoterOid = rootState.authModule.account?.promoterOid!
    const { $axios, $arNotification } = getContext()
    try {
      const { data, status } = await $axios.get(
        `/promoter/${promoterOid}/task`,
        {
          params: {
            $top: 1,
            $skip: 0,
            $filter: `promoterOid=${promoterOid} AND messageListOid=${messageListOid} AND (status="pending" OR status="in-progress") AND (name ILIKE "%mass-edit%" OR name ILIKE "%background-fan-import-csv%") AND sysActivep=true`,
            $orderby: `started DESC, oid DESC`,
          },
        },
      )

      return data
    }
    catch (error) {
      console.error(error)
    }
  },
}
